import { ESTIMATE_COUNTRIES, ESTIMATE_MESSAGE_TYPES, GST_RATE, MAX_ESTIMATE_MESSAGE_COUNT, MAX_ESTIMATE_TOTAL_MESSAGES, MAX_RECHARGE_AMOUNT, MIN_RECHARGE_AMOUNT } from './walletConstants';

export function formatWalletCurrency(amount, options = {}) {
  const { minimumFractionDigits = 2, maximumFractionDigits = 2, withRupee = false } = options;
  const formatted = Number(amount).toLocaleString('en-IN', { minimumFractionDigits, maximumFractionDigits });
  return withRupee ? `₹${formatted}` : formatted;
}

export function isLowBalance(balance, threshold = 500) {
  return balance < threshold;
}

export function calculateGstAmount(baseAmount) {
  return baseAmount * GST_RATE;
}

export function calculateTotalWithGst(baseAmount) {
  return baseAmount * (1 + GST_RATE);
}

export function getTotalMessageCount(inputs) {
  return (inputs.marketing || 0) + (inputs.utility || 0) + (inputs.auth || 0) + (inputs.service || 0);
}

const VALID_COUNTRY_CODES = new Set(ESTIMATE_COUNTRIES.map((c) => c.code));

function normalizeMessageCount(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.min(Math.floor(n), MAX_ESTIMATE_MESSAGE_COUNT);
}

export function normalizeEstimateMessageInput(rawValue) {
  if (rawValue === '' || rawValue === null || rawValue === undefined) return 0;
  const val = parseInt(String(rawValue), 10);
  if (!Number.isFinite(val) || val < 0) return 0;
  return Math.min(val, MAX_ESTIMATE_MESSAGE_COUNT);
}

export function parseRechargeCreditsInput(raw) {
  const digits = String(raw ?? '').replace(/[^0-9]/g, '');
  if (digits === '') {
    return { value: '', numeric: 0 };
  }
  const numeric = parseInt(digits, 10);
  if (!Number.isFinite(numeric)) {
    return { value: '', numeric: 0 };
  }
  const clamped = Math.min(numeric, MAX_RECHARGE_AMOUNT);
  return { value: String(clamped), numeric: clamped };
}

export function validateRechargeCredits(numeric) {
  if (!numeric || numeric < MIN_RECHARGE_AMOUNT) {
    return { error: `Minimum ${MIN_RECHARGE_AMOUNT.toLocaleString('en-IN')} credits required.` };
  }
  if (numeric > MAX_RECHARGE_AMOUNT) {
    return {
      error: `Maximum ${MAX_RECHARGE_AMOUNT.toLocaleString('en-IN')} credits allowed per recharge.`,
    };
  }
  return { amount: numeric };
}

/**
 * Builds and validates ESTIMATE_COST payload from UI inputs.
 * API shape: { campaignBreakdown: [{ country, marketing?, utility?, authentication?, service? }] }
 */
export function buildEstimateCostPayload({ country, inputs }) {
  if (!country || !VALID_COUNTRY_CODES.has(country)) {
    return { error: 'Select a valid country / region.' };
  }

  if (!inputs || typeof inputs !== 'object') {
    return { error: 'Enter messaging volume to estimate cost.' };
  }

  const entry = { country };
  let totalMessages = 0;

  for (const type of ESTIMATE_MESSAGE_TYPES) {
    const count = normalizeMessageCount(inputs[type.id]);
    if (count === null) {
      return { error: `${type.label} must be a non-negative whole number.` };
    }
    if (count > MAX_ESTIMATE_MESSAGE_COUNT) {
      return {
        error: `${type.label} cannot exceed ${MAX_ESTIMATE_MESSAGE_COUNT.toLocaleString('en-IN')}.`,
      };
    }
    if (count > 0) {
      entry[type.apiKey] = count;
      totalMessages += count;
    }
  }

  if (totalMessages <= 0) {
    return { error: 'Enter at least one message volume greater than zero.' };
  }

  if (totalMessages > MAX_ESTIMATE_TOTAL_MESSAGES) {
    return {
      error: `Total monthly volume cannot exceed ${MAX_ESTIMATE_TOTAL_MESSAGES.toLocaleString('en-IN')} messages.`,
    };
  }

  return {
    payload: {
      campaignBreakdown: [entry],
    },
  };
}

export function formatLedgerDate(epochMs) {
  if (!epochMs) return '—';
  try {
    return new Date(epochMs).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
}

const LEDGER_PURPOSE_META = {
  WALLET_RECHARGE: { type: 'Recharge', description: 'Wallet Top-up', isPositive: true },
};

// Maps the GET_LEDGER `history` response into the shape WalletTransactionLedger renders.
// The API does not return a running `balanceAfter`, so it is left null and shown as '—'.
export function mapLedgerToTransactions(history = []) {
  if (!Array.isArray(history)) return [];
  return history.map((entry) => {
    const meta = LEDGER_PURPOSE_META[entry.paymentPurpose] || {
      type: 'Payment',
      description: entry.paymentPurpose || 'Payment',
      isPositive: false,
    };
    return {
      id: entry.paymentId,
      date: formatLedgerDate(entry.createdAt),
      createdAt: entry.createdAt,
      type: meta.type,
      description: meta.description,
      // baseAmount is the wallet credit (pre-GST, in rupees); `amount` is the total charged incl. GST.
      amount: entry.baseAmount ?? 0,
      isPositive: meta.isPositive,
      balanceAfter: null,
      status: entry.status,
      currency: entry.currency,
    };
  });
}

export function loadWalletFromStorage(companyId) {
  try {
    const storageKey = companyId ? `prodyt_wa_wallet_${companyId}` : 'prodyt_wa_wallet_v1';
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch {
    // ignore parse errors
  }
  return null;
}

export function saveWalletToStorage(state, companyId) {
  try {
    const storageKey = companyId ? `prodyt_wa_wallet_${companyId}` : 'prodyt_wa_wallet_v1';
    localStorage.setItem(storageKey, JSON.stringify({
      alertPreferences: state.alertPreferences,
    }));
  } catch {
    // ignore quota errors
  }
}
